// Auth flow: Login (+ forgot-password sub-views), Register, Verify Email,
// Reset Password. Marketing sidebar copy is shared across login + register.
export default {
  en: {
    // ── Shared sidebar marketing copy (Login + Register) ─────────────────────
    marketing: {
      headline: 'Your gateway to {highlight} game keys.',
      headlineHighlight: 'verified',
      liveStock: '{count} games in stock right now',
      tagline:
        'Join the most secure marketplace for digital game keys. Instant access, uncompromising security.',
      benefits: [
        'Instant delivery to your library',
        'EU-Licensed & regionally compliant',
        'Verified keys from authorized distributors',
        'Unrevealed keys are fully refundable',
      ],
    },

    // ── Shared field labels / toggles ─────────────────────────────────────────
    fields: {
      emailAddress: 'Email address',
      emailPlaceholder: "name{'@'}example.com",
      password: 'Password',
      confirmPassword: 'Confirm password',
      newPassword: 'New password',
      confirmNewPassword: 'Confirm new password',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      passwordHint: 'Min 8 characters, with at least one letter and one digit.',
      passwordHintShort: 'Min 8 characters, must contain a letter and a digit.',
      passwordsNoMatch: 'Passwords do not match.',
      passwordsNoMatchYet: "Passwords don't match yet.",
      passwordStrengthWeak: 'Weak',
      passwordStrengthOk: 'OK',
      passwordStrengthStrong: 'Strong',
    },

    // ── Shared error messages ─────────────────────────────────────────────────
    errors: {
      completeCaptcha: 'Please complete the verification.',
      passwordTooWeak:
        'Password must be at least 8 characters and contain a letter and a digit.',
      passwordsDoNotMatch: 'Passwords do not match.',
      mustAgreeTerms: 'You must agree to the Terms of Service and Privacy Policy.',
      linkExpiredGeneric: 'This link has expired or is invalid.',
      enterEmailAndPassword: 'Please enter your email address and password.',
      enterEmail: 'Please enter your email address.',
      enterPassword: 'Please enter your password.',
      invalidEmail: 'Please enter a valid email address.',
      invalidEmailAndEnterPassword: 'Please enter a valid email address and your password.',
      incorrectCredentials: 'Incorrect email or password.',
    },

    // ── Shared footer nav (auth pages embed their own mini-footer) ────────────
    footer: {
      privacy: 'Privacy',
      terms: 'Terms',
      contact: 'Contact',
      rights: '© {year} gamzor.net. All rights reserved.',
    },

    // ── Toasts pushed alongside the success states ───────────────────────────
    toasts: {
      resetLinkSent: 'Reset link sent',
      emailVerified: 'Email verified',
      passwordUpdated: 'Password updated — sign in again',
    },

    // ── Login page ────────────────────────────────────────────────────────────
    login: {
      noAccount: "Don't have an account?",
      signUp: 'Sign up',
      title: 'Welcome back',
      subtitle: 'Log in to access your keys, orders, and wallet.',
      forgotPassword: 'Forgot password?',
      signingIn: 'Signing in…',
      logIn: 'Log in',
      loginFailed: 'Login failed',
    },

    // ── Forgot-password sub-view (inside LoginPage) ───────────────────────────
    forgot: {
      title: 'Reset your password',
      subtitle: "Enter your email and we'll send a reset link.",
      sending: 'Sending…',
      sendResetLink: 'Send reset link',
      backToLogin: 'Back to login',
      couldNotSend: 'Could not send reset email',
    },

    // ── Email-sent sub-view (inside LoginPage) ────────────────────────────────
    sent: {
      title: 'Check your inbox',
      body: "We've sent a reset link to {email}.",
      openEmailApp: 'Open email app',
      backToLogin: 'Back to login',
    },

    // ── Register page ─────────────────────────────────────────────────────────
    register: {
      alreadyHaveAccount: 'Already have an account?',
      signIn: 'Sign in',
      title: 'Create your account',
      subtitle: 'Create your account — it only takes a minute.',
      registerEmailPlaceholder: "commander{'@'}example.com",
      termsAgreement:
        'I agree to the {terms} and {privacy}.',
      termsLink: 'Terms of Service',
      privacyLink: 'Privacy Policy',
      creating: 'Creating…',
      createAccount: 'Create Account',
      registrationFailed: 'Registration failed',
      refundNotice:
        'By registering, you acknowledge our 14-day refund policy on unrevealed keys.',
    },

    // ── Verify Email page ─────────────────────────────────────────────────────
    verify: {
      verifying: {
        title: 'Verifying your email…',
        body: 'One moment while we confirm the link.',
      },
      success: {
        title: 'Email verified!',
        bodyKyc:
          'Your email is now confirmed. You can purchase items up to €50. For larger purchases, complete KYC in your account.',
        body: "Your email is now confirmed. You're all set to start shopping.",
        goToDashboard: 'Go to dashboard',
        browseCatalog: 'Browse catalog',
      },
      error: {
        title: 'Verification link expired',
        missingToken:
          'Missing verification token. The link may have been pasted incorrectly.',
        expiredOrInvalid:
          'This link has expired or is invalid. Verification links are valid for 24 hours.',
        resend: 'Resend verification email',
        resendSent: 'Verification email sent',
        resendConfirm: 'Verification email sent. Check your inbox.',
        backToLogin: 'Back to login',
      },
    },

    // ── Reset Password page ───────────────────────────────────────────────────
    reset: {
      invalidToken: {
        title: 'Link is no longer valid',
        missingToken: 'Missing reset token. Use the link in your reset email.',
        expiredOrInvalid: 'This link has expired or is invalid.',
        backToLogin: 'Back to login',
      },
      success: {
        title: 'Password updated',
        body: 'You can now sign in with your new password. Other sessions have been signed out for safety.',
        continueToLogin: 'Continue to login',
      },
      form: {
        title: 'Reset your password',
        subtitle: 'Choose a new password for your account.',
        saving: 'Saving…',
        resetPassword: 'Reset password',
        backToLogin: 'Back to login',
      },
    },
  },

  cs: {
    // ── Shared sidebar marketing copy ─────────────────────────────────────────
    marketing: {
      headline: 'Vaše brána k {highlight} herním klíčům.',
      headlineHighlight: 'ověřeným',
      liveStock: '{count} her právě skladem',
      tagline:
        'Připojte se k nejbezpečnějšímu tržišti pro digitální herní klíče. Okamžitý přístup, bezkompromisnní bezpečnost.',
      benefits: [
        'Okamžité doručení do vaší knihovny',
        'Licencováno v EU, v souladu s regionálními předpisy',
        'Ověřené klíče od autorizovaných distributorů',
        'Neodhalené klíče jsou plně refundovatelné',
      ],
    },

    // ── Shared field labels / toggles ─────────────────────────────────────────
    fields: {
      emailAddress: 'E-mailová adresa',
      emailPlaceholder: "jmeno{'@'}priklad.cz",
      password: 'Heslo',
      confirmPassword: 'Potvrdit heslo',
      newPassword: 'Nové heslo',
      confirmNewPassword: 'Potvrdit nové heslo',
      showPassword: 'Zobrazit heslo',
      hidePassword: 'Skrýt heslo',
      passwordHint: 'Minimálně 8 znaků, alespoň jedno písmeno a jedna číslice.',
      passwordHintShort: 'Minimálně 8 znaků, musí obsahovat písmeno a číslici.',
      passwordsNoMatch: 'Hesla se neshodují.',
      passwordsNoMatchYet: 'Hesla se zatím neshodují.',
      passwordStrengthWeak: 'Slabé',
      passwordStrengthOk: 'Dobré',
      passwordStrengthStrong: 'Silné',
    },

    // ── Shared error messages ─────────────────────────────────────────────────
    errors: {
      completeCaptcha: 'Prosím dokončete ověření.',
      passwordTooWeak:
        'Heslo musí mít alespoň 8 znaků a obsahovat písmeno a číslici.',
      passwordsDoNotMatch: 'Hesla se neshodují.',
      mustAgreeTerms:
        'Musíte souhlasit s Podmínkami služby a Zásadami ochrany osobních údajů.',
      linkExpiredGeneric: 'Tento odkaz vypršel nebo je neplatný.',
      enterEmailAndPassword: 'Zadejte prosím e-mailovou adresu a heslo.',
      enterEmail: 'Zadejte prosím e-mailovou adresu.',
      enterPassword: 'Zadejte prosím heslo.',
      invalidEmail: 'Zadejte prosím platnou e-mailovou adresu.',
      invalidEmailAndEnterPassword: 'Zadejte prosím platnou e-mailovou adresu a heslo.',
      incorrectCredentials: 'Nesprávný e-mail nebo heslo.',
    },

    // ── Shared footer nav ─────────────────────────────────────────────────────
    footer: {
      privacy: 'Soukromí',
      terms: 'Podmínky',
      contact: 'Kontakt',
      rights: '© {year} gamzor.net. Všechna práva vyhrazena.',
    },

    // ── Toasty k úspěšným stavům ──────────────────────────────────────────────
    toasts: {
      resetLinkSent: 'Odkaz pro obnovení odeslán',
      emailVerified: 'E-mail ověřen',
      passwordUpdated: 'Heslo změněno — přihlaste se znovu',
    },

    // ── Login page ────────────────────────────────────────────────────────────
    login: {
      noAccount: 'Nemáte účet?',
      signUp: 'Zaregistrujte se',
      title: 'Vítejte zpět',
      subtitle: 'Přihlaste se a získejte přístup ke svým klíčům, objednávkám a peněžence.',
      forgotPassword: 'Zapomněli jste heslo?',
      signingIn: 'Přihlašování…',
      logIn: 'Přihlásit se',
      loginFailed: 'Přihlášení selhalo',
    },

    // ── Forgot-password sub-view ───────────────────────────────────────────────
    forgot: {
      title: 'Obnovení hesla',
      subtitle: 'Zadejte svůj e-mail a zašleme vám odkaz pro obnovení hesla.',
      sending: 'Odesílání…',
      sendResetLink: 'Odeslat odkaz pro obnovení',
      backToLogin: 'Zpět na přihlášení',
      couldNotSend: 'Odkaz pro obnovení hesla se nepodařilo odeslat',
    },

    // ── Email-sent sub-view ────────────────────────────────────────────────────
    sent: {
      title: 'Zkontrolujte svou schránku',
      body: 'Odeslali jsme odkaz pro obnovení hesla na adresu {email}.',
      openEmailApp: 'Otevřít e-mailový klient',
      backToLogin: 'Zpět na přihlášení',
    },

    // ── Register page ─────────────────────────────────────────────────────────
    register: {
      alreadyHaveAccount: 'Již máte účet?',
      signIn: 'Přihlásit se',
      title: 'Vytvořte si účet',
      subtitle: 'Vytvořte si účet — zabere to jen chvilku.',
      registerEmailPlaceholder: "velitel{'@'}priklad.cz",
      termsAgreement: 'Souhlasím s {terms} a {privacy}.',
      termsLink: 'Podmínkami služby',
      privacyLink: 'Zásadami ochrany osobních údajů',
      creating: 'Vytváření…',
      createAccount: 'Vytvořit účet',
      registrationFailed: 'Registrace selhala',
      refundNotice:
        'Registrací berete na vědomí naši 14denní zásadu vrácení peněz za neodhalené klíče.',
    },

    // ── Verify Email page ─────────────────────────────────────────────────────
    verify: {
      verifying: {
        title: 'Ověřování e-mailu…',
        body: 'Chvilku počkejte, potvrzujeme odkaz.',
      },
      success: {
        title: 'E-mail ověřen!',
        bodyKyc:
          'Váš e-mail byl potvrzen. Můžete nakupovat položky do výše 50 €. Pro větší nákupy dokončete ověření KYC ve svém účtu.',
        body: 'Váš e-mail byl potvrzen. Vše je připraveno k nákupu.',
        goToDashboard: 'Přejít na přehled',
        browseCatalog: 'Procházet katalog',
      },
      error: {
        title: 'Platnost ověřovacího odkazu vypršela',
        missingToken:
          'Chybí ověřovací token. Odkaz mohl být vložen nesprávně.',
        expiredOrInvalid:
          'Tento odkaz vypršel nebo je neplatný. Ověřovací odkazy jsou platné 24 hodin.',
        resend: 'Znovu odeslat ověřovací e-mail',
        resendSent: 'Ověřovací e-mail odeslán',
        resendConfirm: 'Ověřovací e-mail odeslán. Zkontrolujte svou schránku.',
        backToLogin: 'Zpět na přihlášení',
      },
    },

    // ── Reset Password page ───────────────────────────────────────────────────
    reset: {
      invalidToken: {
        title: 'Odkaz již není platný',
        missingToken:
          'Chybí token pro obnovení hesla. Použijte odkaz z e-mailu pro obnovení.',
        expiredOrInvalid: 'Tento odkaz vypršel nebo je neplatný.',
        backToLogin: 'Zpět na přihlášení',
      },
      success: {
        title: 'Heslo bylo změněno',
        body: 'Nyní se můžete přihlásit novým heslem. Z bezpečnostních důvodů byly ostatní relace odhlášeny.',
        continueToLogin: 'Pokračovat na přihlášení',
      },
      form: {
        title: 'Obnovení hesla',
        subtitle: 'Zvolte nové heslo pro svůj účet.',
        saving: 'Ukládání…',
        resetPassword: 'Obnovit heslo',
        backToLogin: 'Zpět na přihlášení',
      },
    },
  },
};
